import React, { ReactElement, useEffect, useState } from 'react';
import {
  Body,
  Companies,
  GlobalTemplate,
  Headline,
  Link,
  Meta,
  Text,
} from '../components';
import G2Callout from '../components/callouts/g2';
import WhyStark from '../components/callouts/why-stark';
import './index.css';

export default function Homepage(): ReactElement {
  const [selectedWorkflow, setSelectedWorkflow] = useState('designer');

  useEffect(() => {}, [selectedWorkflow]);

  const list = {
    visible: {
      opacity: 1,
      transition: { when: 'beforeChildren', staggerChildren: 0.1 },
    },
    hidden: { opacity: 0, transition: { when: 'afterChildren' } },
  };

  const item = {
    visible: { opacity: 1, scale: 1, y: 0 },
    hidden: { opacity: 0, scale: 0.8, y: 20 },
  };

  return (
    <GlobalTemplate pageBgColor="bone" navBgColor="purple-500">
      <Meta ogImgUrl="https://images.getstark.co/marketing/meta/homepage.png">
        <meta name="theme-color" content="#381FD1"></meta>
      </Meta>

      <header
        className="homepage__gradient relative mt-[-65px] bg-base-purple-500 bg-cover bg-no-repeat pt-4 md:pt-16"
        style={{
          backgroundImage:
            "url('https://images.getstark.co/marketing/headers/gradient-bg.webp')",
        }}>
        <div className="z-10 mb-12 mt-[88px] flex flex-col items-center justify-center pb-12 sm:mb-0 sm:pb-[316px]">
          <Headline
            level="hero"
            color="white"
            className="max-w-[650px] text-center sm:!text-[110px] sm:!leading-[121px]">
            Super&shy;charge Access&shy;ibility
          </Headline>
          <Body
            className="mb-8 mt-4 max-w-[600px] px-2 text-center"
            color="white">
            The world’s best companies – from startups to the Fortune 100 –
            trust Stark to accelerate time to digital accessibility compliance.
          </Body>

          <div className="homepage__header__ctas relative flex flex-wrap gap-4">
            <a
              href="https://account.getstark.co/sign-in"
              className="link link--button link--button-yellow flex">
              Sign up for free
            </a>
          </div>
        </div>
        <div className="-mt-11 flex items-center justify-center">
          <img
            src="https://images.getstark.co/marketing/headers/homepage-header-2023-4.webp"
            alt=""
            className="pointer-events-none absolute bottom-0 hidden w-full min-w-[1440px] max-w-[1440px] -translate-x-6 sm:block"
          />
        </div>
      </header>

      <main>
        <section
          className="flex justify-center p-4"
          aria-labelledby="headline-speedup">
          <div style={{ maxWidth: 1200, width: 1200 }}>
            <img
              src="https://images.getstark.co/marketing/features/speed-up-your-accessibility-feature-2023.png?updatedAt=1709782332170"
              alt="Stark shown at different sizes to highlight how it can be used anywhere. On each screen are different examples that show charts, number of issues, and detailed breakdowns of accessibility issues Stark found."
              className="relative z-20 -mt-52 hidden w-full pb-14 sm:block"
            />

            <div className="flex w-full justify-center">
              <Headline
                id="headline-speedup"
                level="1"
                color="blue"
                className="relative z-10 mt-12 max-w-[945px] text-center sm:mt-0">
                Speed up your accessibility workflow{' '}
                <span className="homepage-mark">from months to minutes</span>
              </Headline>
            </div>
            <Body
              className="mb-10 ml-auto mr-auto mt-10 text-center"
              color="blue"
              style={{ maxWidth: 734 }}>
              Designers, developers, product managers, and accessibility experts
              at 50,000+ companies use Stark’s suite of integrated accessibility
              tools in their daily product development cycle. Join them today!
            </Body>
          </div>
        </section>

        <Companies />

        <section
          className="mt-16 flex justify-center p-4 sm:mt-36"
          aria-labelledby="headline-endtoend">
          <div
            className="z-10"
            style={{
              maxWidth: 960,
              width: 960,
            }}>
            <Text level="mono" color="purple" className="mb-8 text-center">
              Explore the Stark platform
            </Text>
            <Headline
              id="headline-endtoend"
              level="2"
              className="mb-8 text-center">
              <span className="block">
                The only{' '}
                <span className="homepage-mark homepage-mark__inverse">
                  end-to-end solution
                </span>
              </span>
              <span>from design and code to live product</span>
            </Headline>

            <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 md:grid-cols-4">
              <div>
                <img
                  src="https://images.getstark.co/marketing/illos/plugins-integrations-illo-2023.png"
                  alt=""
                  className="max-w-[205px] pb-3"
                />
                <Text level="mono" color="purple" className="!text-[0.8rem]">
                  Speed up design & Dev
                </Text>
                <Headline level="4" color="purple" className="my-3">
                  Plug-ins & Integrations
                </Headline>
                <Text>
                  Meet your team where they work, from Figma and Sketch to
                  GitHub and their browser of choice. An accessibility workflow
                  built right into the tools they love. No need for them to
                  switch or relearn.
                </Text>
              </div>

              <div>
                <img
                  src="https://images.getstark.co/marketing/illos/projects-reports-insights-illo-2023.png"
                  alt=""
                  className="max-w-[205px] pb-3"
                />
                <Text level="mono" color="purple" className="!text-[0.8rem]">
                  Improve collaboration
                </Text>
                <Headline level="4" color="purple" className="my-3">
                  Projects, Reports & Insights
                </Headline>
                <Text>
                  Manage your team’s accessibility workflow in the Stark Web
                  app. Monitor and manage progress with real-time reports and
                  insights across all your projects from design to code.
                </Text>
              </div>

              <div>
                <img
                  src="https://images.getstark.co/marketing/illos/ai-powered-automation-illo-2023.png"
                  alt=""
                  className="max-w-[205px] pb-3"
                />
                <Text level="mono" color="purple" className="!text-[0.8rem]">
                  Accelerate workflows
                </Text>
                <Headline level="4" color="purple" className="my-3">
                  AI-powered Automation
                </Headline>
                <Text>
                  Automated continuous scanning of design files and code
                  repositories combined with AI-powered suggestions accelerates
                  finding and fixing of accessibility issues in record time.
                </Text>
              </div>

              <div>
                <img
                  src="https://images.getstark.co/marketing/illos/compliance-center-illo-2023.png"
                  alt=""
                  className="max-w-[205px] pb-3"
                />
                <Text level="mono" color="purple" className="!text-[0.8rem]">
                  Manage at Scale
                </Text>
                <Headline level="4" color="purple" className="my-3">
                  Compliance Center
                </Headline>
                <Text>
                  Manage your company’s accessibility posture in one central
                  hub. Assign and document individual controls based on the
                  regulatory frameworks. Access evidence for easy auditing.
                </Text>
              </div>
            </div>

            <div className="my-8 flex w-full justify-center">
              <Link linkType="button" href="https://account.getstark.co">
                Get started for free
              </Link>
            </div>
          </div>
        </section>

        <section
          className="mt-16 flex justify-center p-4 sm:mt-36"
          id="learn-more"
          aria-labelledby="headline-continuous">
          <div
            style={{
              maxWidth: 1200,
              width: 1200,
            }}>
            <div className="flex flex-col items-center justify-center text-center">
              <img
                src="https://images.getstark.co/marketing/features/continuous-accessibility-feature-2023.webp"
                alt="Stark's reports and insights UI shown with an eCommerce shop example. Surrounding it are avatars of people commenting on the progress of their accessibility for the site."
                className="w-full pb-14"
              />
              <Headline
                id="headline-continuous"
                level="1"
                color="blue"
                className="max-w-[700px]">
                <span className="homepage-mark">Continuous Accessibility</span>{' '}
                for your entire team
              </Headline>
              <Body className="mb-8 mt-4 max-w-[780px]" color="blue">
                Get real-time reports that help manage accessibility at scale
                without slowing down the velocity of your team. Stark gives your
                up-to-date insights into the accessibility of all your design
                files, code repositories and live sites or applications, all in
                one central place for efficient collaboration across
                departments.
              </Body>
              <Link linkType="button" href="/pricing">
                Start a free team trial
              </Link>
            </div>
          </div>
        </section>

        <section
          className="mt-16 flex justify-center p-4 sm:mt-36"
          aria-labelledby="headline-integrated">
          <div
            style={{
              maxWidth: 1200,
              width: 1200,
            }}>
            <div className="flex flex-col items-center justify-center text-center">
              <img
                src="https://images.getstark.co/marketing/features/integrated-into-the-tools-feature-2023.webp"
                alt="Numerous bubbles of logos with Stark at the center. Surrounding it are logos for design platforms like Sketch and Figma and browsers like Chrome and Edge."
                className="w-full pb-14"
              />
              <Headline
                id="headline-integrated"
                level="1"
                color="blue"
                className="max-w-[700px]">
                <span className="homepage-mark">Integrated into the tools</span>{' '}
                you already use
              </Headline>

              <Body className="mb-8 mt-4 max-w-[780px]" color="blue">
                Whether it's Figma or Sketch, your favorite web browser, or even
                GitHub and beyond, Stark integrates into the tools your team
                already uses for maximum efficiency and no learning curve. Our
                AI-powered integrations help your teammates find and fix
                accessibility issues as early as the first designs and all
                across the development cycle.
              </Body>
              <Link linkType="button" href="https://account.getstark.co">
                Get started for free
              </Link>
            </div>
          </div>
        </section>

        <section
          className="mt-16 flex justify-center p-4 sm:mt-36"
          aria-labelledby="headline-enterprise">
          <div
            style={{
              maxWidth: 1200,
              width: 1200,
            }}>
            <div className="flex flex-col items-center justify-center text-center">
              <img
                src="https://images.getstark.co/marketing/features/enterprise-grade-platform-feature-2023.png"
                alt="An illustrated solar system with a padlock as the sun and GDPR, Zero Trust, SSO, and SOC2 as planets."
                className="w-full pb-14"
              />
              <Headline
                id="headline-enterprise"
                level="1"
                color="blue"
                className="max-w-[780px]">
                <span className="homepage-mark">
                  An enterprise-grade platform
                </span>{' '}
                you can trust
              </Headline>
              <Body className="mb-8 mt-4 max-w-[780px]" color="blue">
                The entire Stark platform and all our integrations are built
                with enterprise-grade security. From SSO and our fully GDPR /
                SOC2-certified platform to our zero-trust policy that underpins
                all our tool integrations, we ensure maximum security and
                privacy for our customers’ data and IP.
              </Body>
              <Link linkType="button" href="/pricing">
                Start a free team trial
              </Link>
            </div>
          </div>
        </section>

        <G2Callout />

        <WhyStark />

        <section
          className="relative mb-28 mt-16 flex justify-center p-4 pt-0"
          aria-labelledby="headline-scale">
          <div className="relative">
            <svg
              width="445"
              height="410"
              viewBox="0 0 445 410"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="absolute -left-64 -top-20"
              aria-hidden="true">
              <path
                d="M46.9335 338.378C30.213 326.986 16.571 311.079 8.5949 292.456C-9.37173 250.476 0.866772 195.191 41.9466 172.708C83.7279 149.84 135.827 142.321 169.401 106.081C195.151 78.2885 205.454 36.7387 236.894 15.6064C288.077 -18.8073 361.822 12.7332 400.592 51.8842C416.519 67.9692 428.511 88.7202 430.825 111.237C433.139 133.755 425.957 156.059 422.905 178.508C416.221 227.494 441.62 264.257 444.197 310.598C448.551 388.984 362.658 423.241 298.438 405C244.721 389.738 198.832 356.367 141.164 355.961C128.052 355.865 114.918 357.677 101.828 357.128C82.1499 356.057 63.154 349.568 46.9335 338.378Z"
                fill="#381FD1"
              />
            </svg>
            <img
              className="absolute -bottom-32 -right-64"
              alt=""
              role="presentation"
              src="/homepage-blob-3.png"
              aria-hidden="true"
            />
            <div
              className="relative lg:w-[1024px]"
              style={{
                maxWidth: 1024,
              }}>
              <div className="grid w-full grid-cols-1 rounded-[40px] bg-purple-50 p-7 sm:p-14 lg:grid-cols-[1fr_550px]">
                <div className="relative hidden lg:block">
                  {selectedWorkflow === 'designer' && (
                    <img
                      src={`https://images.getstark.co/marketing/features/workflow-designer.webp?updatedAt=1697384167702`}
                      alt=""
                      className="absolute right-0 top-2 h-[740px] max-h-full max-w-fit opacity-0"
                      aria-describedby="streamline-designer"
                      onLoad={(e) =>
                        e.currentTarget.classList.add(
                          'animate-streamlining-section-fade-in'
                        )
                      }
                    />
                  )}
                  {selectedWorkflow === 'developer' && (
                    <img
                      src={`https://images.getstark.co/marketing/features/workflow-developer.webp?updatedAt=1697384167702`}
                      alt=""
                      className="absolute right-0 top-2 h-[740px] max-h-full max-w-fit opacity-0"
                      aria-describedby="streamline-developer"
                      onLoad={(e) =>
                        e.currentTarget.classList.add(
                          'animate-streamlining-section-fade-in'
                        )
                      }
                    />
                  )}
                  {selectedWorkflow === 'pm' && (
                    <img
                      src={`https://images.getstark.co/marketing/features/workflow-pm-2.webp`}
                      alt=""
                      className="absolute right-0 top-2 h-[740px] max-h-full max-w-fit opacity-0"
                      aria-describedby="streamline-pm"
                      onLoad={(e) =>
                        e.currentTarget.classList.add(
                          'animate-streamlining-section-fade-in'
                        )
                      }
                    />
                  )}
                  {selectedWorkflow === 'compliance' && (
                    <img
                      src={`https://images.getstark.co/marketing/features/workflow-compliance.webp?updatedAt=1697384167702`}
                      alt=""
                      className="absolute right-0 top-2 h-[740px] max-h-full max-w-fit opacity-0"
                      aria-describedby="streamline-compliance"
                      onLoad={(e) =>
                        e.currentTarget.classList.add(
                          'animate-streamlining-section-fade-in'
                        )
                      }
                    />
                  )}
                </div>
                <div>
                  <Text level="mono" color="purple" className="mb-8 ml-6">
                    Streamlining workflows, consolidating tools
                  </Text>
                  <Headline
                    id="headline-scale"
                    level="1"
                    color="blue"
                    className="mb-12 ml-6">
                    We scale with you{' '}
                    <span className="homepage-mark">across departments</span>
                  </Headline>

                  <div className="grid grid-cols-1">
                    <Link
                      href="/for-designers"
                      className={`group rounded-[20px] p-6 !no-underline transition-colors ${
                        selectedWorkflow === 'designer' && 'lg:bg-[#E5E0FF]'
                      }`}
                      onMouseEnter={() => setSelectedWorkflow('designer')}
                      onFocus={() => setSelectedWorkflow('designer')}
                      tabIndex={0}>
                      <Headline
                        level="4"
                        color="purple"
                        className="group-hover:underline">
                        Designers
                      </Headline>
                      <Text id="streamline-designer">
                        Create accessible designs in record time right in Figma,
                        Sketch, and Adobe XD.
                      </Text>
                    </Link>

                    <Link
                      href="/for-developers"
                      className={`group rounded-[20px] p-6 !no-underline transition-colors ${
                        selectedWorkflow === 'developer' && 'lg:bg-[#E5E0FF]'
                      }`}
                      onMouseEnter={() => setSelectedWorkflow('developer')}
                      onFocus={() => setSelectedWorkflow('developer')}
                      tabIndex={0}>
                      <Headline
                        level="4"
                        color="purple"
                        className="group-hover:underline">
                        Developers
                      </Headline>
                      <Text id="streamline-developer">
                        Test and audit your code from early implementation to
                        live product.
                      </Text>
                    </Link>

                    <Link
                      href="/for-product-managers"
                      className={`group rounded-[20px] p-6 !no-underline transition-colors ${
                        selectedWorkflow === 'pm' && 'lg:bg-[#E5E0FF]'
                      }`}
                      onMouseEnter={() => setSelectedWorkflow('pm')}
                      onFocus={() => setSelectedWorkflow('pm')}
                      tabIndex={0}>
                      <Headline
                        level="4"
                        color="purple"
                        className="group-hover:underline">
                        Product Managers
                      </Headline>
                      <Text id="streamline-pm">
                        Manage accessibility in real-time across all your
                        projects.
                      </Text>
                    </Link>

                    <Link
                      href="/for-compliance-managers"
                      className={`group rounded-[20px] p-6 !no-underline transition-colors ${
                        selectedWorkflow === 'compliance' && 'lg:bg-[#E5E0FF]'
                      }`}
                      onMouseEnter={() => setSelectedWorkflow('compliance')}
                      onFocus={() => setSelectedWorkflow('compliance')}
                      tabIndex={0}>
                      <Headline
                        level="4"
                        color="purple"
                        className="group-hover:underline">
                        Compliance Managers
                      </Headline>
                      <Text id="streamline-compliance">
                        Monitor, manage and audit your accessibility posture in
                        one place.
                      </Text>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </GlobalTemplate>
  );
}
